/**
 * 素材選択セクション
 *
 * タブ構造（UserStockと同じカテゴリ）:
 * - ALBUM: アップロードした写真（非SVG、非AI）
 * - DRAWING: おえかき（SVG、非AI）
 * - AI: AI生成画像
 *
 * スタイルルール：
 * - 選択ボタン: hover不使用、transition不使用、btnDelay不使用
 * - アクションボタン（アップロード）: btnDelay使用
 */

import { useMemo, useRef, ChangeEventHandler } from "react";
import { useQuery } from "@tanstack/react-query";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import { Asset } from "app/types";
import { usePickImageAsset } from "app/hook";
import I18n from "common/i18n";
import { usePlaySound, formatStorageUsage } from "common/utils";
import { useAssetLimit } from "features/subscription";
import { StockTabIndex } from "features/creator/types";
import { getUserAssets, AssetsResponse } from "features/user/api";
import { IconSource } from "../types";

interface MaterialSectionProps {
  /** 対象ユーザーのUID */
  targetUid: string;
  /** 選択中のアイコンURL */
  selectedIcon?: string | null;
  /** アセット選択コールバック */
  onAssetSelect: (url: string, source: IconSource) => void;
  /** 現在のタブ */
  tab: StockTabIndex;
  /** タブ変更コールバック */
  onTabChange: (tab: StockTabIndex) => void;
  /** AI生成モーダルを開くコールバック（AIタブ用） */
  onOpenAIModal?: () => void;
}

const TAB_OPTIONS = [
  { key: StockTabIndex.ALBUM, labelKey: "MSG_CREATOR_MENU_ASSET_CATEGORY_ALBUM_TITLE" },
  { key: StockTabIndex.DRAWING, labelKey: "MSG_CREATOR_MENU_ASSET_CATEGORY_DRAWING_TITLE" },
  { key: StockTabIndex.AI, labelKey: "MSG_USER_STOCK_AI_TAB" },
];

export const MaterialSection = ({
  targetUid,
  selectedIcon,
  onAssetSelect,
  tab,
  onTabChange,
  onOpenAIModal,
}: MaterialSectionProps) => {
  const play = usePlaySound();
  const assetLimit = useAssetLimit();
  const isUnlimited = assetLimit.unlimited;
  const limitValue = assetLimit.limit;
  const usedValue = assetLimit.used;
  const isOverLimit = !isUnlimited && usedValue > limitValue;
  const inputRef = useRef<HTMLInputElement>(null);
  const pickImageAsset = usePickImageAsset();

  // タブ選択: btnDelay不使用
  const handleTabChange = (newTab: StockTabIndex) => {
    play();
    onTabChange(newTab);
  };

  // 画像アップロード
  const pickImage: ChangeEventHandler<HTMLInputElement> = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      await pickImageAsset(
        file,
        targetUid,
        async () => {
          if (inputRef.current) {
            inputRef.current.value = "";
          }
        },
        () => {
          if (inputRef.current) {
            inputRef.current.value = "";
          }
        },
      );
    }
  };

  // アセット取得
  const { data, isLoading } = useQuery<AssetsResponse>({
    queryKey: ["creator/getUserAssets", targetUid],
    queryFn: () => getUserAssets({ uid: targetUid }),
    enabled: !!targetUid,
    placeholderData: { assets: [], totalSize: 0 },
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const assets = data?.assets ?? [];

  // AI判定
  const isAIAsset = (asset: Asset) => asset.tags?.startsWith("ai_generated:");

  // タブでフィルタリング（UserStockと同じロジック）
  const filteredAssets = useMemo(() => {
    return assets
      .filter((a) => {
        if (tab === StockTabIndex.AI) return isAIAsset(a);
        if (tab === StockTabIndex.DRAWING) return /.svg/.test(a.url) && !isAIAsset(a);
        return !/.svg/.test(a.url) && !isAIAsset(a); // ALBUM
      })
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [assets, tab]);

  // ソースを判定
  const determineSource = (asset: Asset): IconSource => {
    if (isAIAsset(asset)) {
      return IconSource.HISTORY;
    }
    return IconSource.UPLOAD;
  };

  return (
    <div className="flex-col-el h-full min-h-0">
      {/* 容量表示 */}
      {!isUnlimited && (
        <div className="flex-row-el justify-end w-full mb-1">
          <span
            className={`text-body text-font-caption2 ${isOverLimit ? "text-red-100" : "text-gray-80"}`}
          >
            {formatStorageUsage(usedValue, limitValue, isUnlimited)}
          </span>
        </div>
      )}
      {/* タブナビゲーション（プリセットと同じデザイン） */}
      <div className="flex-row-center h-[74px] mb-4 bg-gray2-10 rounded-[12px] border-[4px] border-solid border-white">
        {TAB_OPTIONS.map((option, index) => {
          const isActive = tab === option.key;
          const isFirst = index === 0;
          const isLast = index === TAB_OPTIONS.length - 1;
          return (
            <div
              key={option.key}
              onClick={() => handleTabChange(option.key)}
              className={`flex-col-center cursor-pointer flex-1 h-full ${
                isActive ? "bg-green-alt" : ""
              } ${isFirst ? "rounded-l-[8px]" : ""} ${isLast ? "rounded-r-[8px]" : ""}`}
            >
              <p className="flex-col-center text text-textcolor-black !text-[20px] !leading-[22px]">
                {I18n.t(option.labelKey)}
              </p>
            </div>
          );
        })}
      </div>

      {/* コンテンツ */}
      <div className="flex-col-el flex-1 overflow-y-auto min-h-0 relative">
        {isLoading ? (
          <div className="flex-col-el flex-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-60" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-asset-auto-fill w-full items-start justify-center gap-2">
              {/* アップロードボタン - ALBUMタブのみ（UserStockと同じデザイン） */}
              {tab === StockTabIndex.ALBUM && (
                <div className="flex-col-el flex-center shrink-0 w-full h-full upsilon gap-3">
                  <div className="btn btn-primary !p-0 !m-0 w-[160px] h-[160px] cursor-pointer bg-green-100 rounded-lg">
                    <div className="flex-col-center w-full h-full">
                      <input
                        ref={inputRef}
                        type="file"
                        onClick={() => play()}
                        onChange={pickImage}
                        accept="image/*"
                        className="absolute w-[160px] h-[160px] opacity-0 z-50 peer cursor-pointer"
                      />
                      <AddPhotoAlternateIcon sx={{ fontSize: 84, color: "white" }} />
                      <p className="text-body text-white text-font-size-body2">
                        {I18n.t("MSG_ASSET_ALBUM_ADD_TITLE")}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* AI生成ボタン - AIタブのみ */}
              {tab === StockTabIndex.AI && onOpenAIModal && (
                <div className="flex-col-el flex-center shrink-0 w-full h-full upsilon gap-3">
                  <div
                    className="btn btn-primary !p-0 !m-0 w-[160px] h-[160px] cursor-pointer bg-blue-100 rounded-lg"
                    onClick={() => {
                      play();
                      onOpenAIModal();
                    }}
                  >
                    <div className="flex-col-center w-full h-full">
                      <span className="material-symbols-outlined !text-[84px] text-white">
                        auto_awesome
                      </span>
                      <p className="text-body text-white text-font-size-body2">
                        {I18n.t("MSG_GENERATE_BTN")}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* アセット一覧 - UserStockと同じデザイン */}
              {filteredAssets.map((asset) => {
                const source = determineSource(asset);
                const isSelected = selectedIcon === asset.url;

                return (
                  <div key={asset.id} className="flex-col-el relative">
                    <div
                      onClick={() => {
                        play();
                        onAssetSelect(asset.url, source);
                      }}
                      className={`flex-col-el relative flex-center w-[160px] h-[160px] bg-beige-20 p-2 border-solid cursor-pointer ${
                        isSelected ? "border-4 border-green-100" : "frame"
                      }`}
                    >
                      <img
                        src={asset.url}
                        alt=""
                        className="w-full h-full object-contain bg-white"
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* 空状態 */}
            {filteredAssets.length === 0 && tab !== StockTabIndex.ALBUM && (
              <div className="flex-col-el flex-center w-full h-full absolute z-10 pointer-events-none">
                <p className="text-body text-gray-80 text-font-caption2-narrow">
                  {tab === StockTabIndex.AI
                    ? I18n.t("MSG_USER_STOCK_AI_EMPTY")
                    : I18n.t("MSG_ASSET_DRAWING_EMPTY_MESSAGE")}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
